import React, { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useEditorState } from '@tiptap/react';
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountTree,
  AddBox,
  ArrowDropDown,
  AutoAwesome,
  Check,
  Checklist,
  Code,
  DataObject,
  Edit as EditIcon,
  FindInPage,
  FormatAlignCenter,
  FormatAlignJustify,
  FormatAlignLeft,
  FormatAlignRight,
  FormatBold,
  FormatColorFill,
  FormatIndentDecrease,
  FormatIndentIncrease,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  FormatStrikethrough,
  FormatUnderlined,
  HorizontalRule,
  Image as ImageIcon,
  IndeterminateCheckBox,
  InsertPageBreak,
  Keyboard,
  Link as LinkIcon,
  Lock,
  LockOpen,
  MoreHoriz,
  Redo,
  Save as SaveIcon,
  Subscript as SubscriptIcon,
  Superscript as SuperscriptIcon,
  TableChart,
  TableRows,
  TableView,
  Toc,
  Undo,
  ViewAgenda,
  WidthFull,
  WidthNormal,
} from '@mui/icons-material';
import { TableInsertGridPopover } from '../tiptap-ui/TableInsertGridPopover';
import { TEMPLATE_VARIABLES } from '../../utils/templateFields';

export const FONT_FAMILIES = [
  { name: 'Default', value: 'Arial, Helvetica, sans-serif' },
  { name: 'Inter', value: 'Inter, system-ui, sans-serif' },
  { name: 'Helvetica', value: 'Helvetica, sans-serif' },
  { name: 'PT Sans', value: '"PT Sans", sans-serif' },
  { name: 'Times New Roman', value: '"Times New Roman", serif' },
  { name: 'Georgia', value: 'Georgia, serif' },
  { name: 'Courier New', value: '"Courier New", monospace' },
  { name: 'Verdana', value: 'Verdana, sans-serif' },
  { name: 'Tahoma', value: 'Tahoma, sans-serif' },
  { name: 'Trebuchet MS', value: '"Trebuchet MS", sans-serif' },
];

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 72];

const TEXT_COLORS = [
  '#1C1B19', '#424242', '#616161', '#757575', '#9E9E9E',
  '#D63B1F', '#E91E63', '#9C27B0', '#4F3BD6', '#3F51B5',
  '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#1F8A4C',
  '#8BC34A', '#CDDC39', '#F2B705', '#FFC107', '#FF9800',
  '#FF5722', '#795548', '#607D8B',
];

const HIGHLIGHT_COLORS = [
  'transparent', '#FFEBEE', '#F3E5F5', '#ECE8FC', '#E3F2FD',
  '#E0F2F1', '#E3F2E8', '#FFFDE7', '#FDEFD6', '#FCE8E2',
  '#FFCDD2', '#F8BBD9', '#D1C4E9', '#C5CAE9', '#BBDEFB',
  '#B2DFDB', '#DCEDC8', '#F0F4C3', '#FFECB3', '#FFCCBC',
];

const TURN_INTO = [
  { value: 'paragraph', label: 'Normal text' },
  { value: 'h1', label: 'Heading 1' },
  { value: 'h2', label: 'Heading 2' },
  { value: 'h3', label: 'Heading 3' },
  { value: 'bulletList', label: 'Bulleted list' },
  { value: 'orderedList', label: 'Numbered list' },
  { value: 'taskList', label: 'Task list' },
  { value: 'blockquote', label: 'Quote' },
  { value: 'codeBlock', label: 'Code block' },
];

/**
 * Resolve the toolbar font label for a CSS font-family value.
 * @param {string|undefined} value - CSS font-family.
 * @returns {string} Font name.
 */
const fontNameFor = (value) => {
  if (!value) return 'Default';
  const norm = (s) => s.replace(/\s+/g, ' ').trim().toLowerCase();
  const found = FONT_FAMILIES.find((f) => norm(f.value) === norm(value));
  return found ? found.name : 'Default';
};

/**
 * Snapshot of toolbar-relevant editor state. Runs on every transaction (cheap isActive checks)
 * so buttons reflect cursor moves without re-rendering the page.
 * @param {{editor: import('@tiptap/core').Editor}} ctx - Selector context.
 * @returns {object} Toolbar state.
 */
const selectToolbarState = ({ editor }) => {
  if (!editor) return null;
  const textStyle = editor.getAttributes('textStyle') || {};
  let turnInto = 'paragraph';
  if (editor.isActive('heading', { level: 1 })) turnInto = 'h1';
  else if (editor.isActive('heading', { level: 2 })) turnInto = 'h2';
  else if (editor.isActive('heading', { level: 3 })) turnInto = 'h3';
  else if (editor.isActive('taskList')) turnInto = 'taskList';
  else if (editor.isActive('bulletList')) turnInto = 'bulletList';
  else if (editor.isActive('orderedList')) turnInto = 'orderedList';
  else if (editor.isActive('blockquote')) turnInto = 'blockquote';
  else if (editor.isActive('codeBlock')) turnInto = 'codeBlock';
  let align = 'left';
  ['center', 'right', 'justify'].forEach((a) => {
    if (editor.isActive({ textAlign: a })) align = a;
  });
  const { $from } = editor.state.selection;
  const top = $from.depth >= 1 ? $from.node(1) : editor.state.selection.node;
  return {
    bold: editor.isActive('bold'),
    italic: editor.isActive('italic'),
    underline: editor.isActive('underline'),
    strike: editor.isActive('strike'),
    code: editor.isActive('code'),
    subscript: editor.isActive('subscript'),
    superscript: editor.isActive('superscript'),
    link: editor.isActive('link'),
    linkHref: editor.getAttributes('link')?.href || '',
    bulletList: editor.isActive('bulletList'),
    orderedList: editor.isActive('orderedList'),
    taskList: editor.isActive('taskList'),
    inList: editor.isActive('listItem') || editor.isActive('taskItem'),
    inTable: editor.isActive('table'),
    isImage: editor.isActive('image'),
    imageAlign: editor.getAttributes('image')?.align || 'center',
    align,
    turnInto,
    fontName: fontNameFor(textStyle.fontFamily),
    fontSize: parseInt(textStyle.fontSize, 10) || 16,
    color: textStyle.color || '',
    canUndo: editor.can().undo(),
    canRedo: editor.can().redo(),
    locked: Boolean(top && top.attrs && top.attrs.tplLocked),
  };
};

/**
 * Small square toolbar button.
 * @param {{label: string, shortcut?: string, active?: boolean, disabled?: boolean, onClick: Function, children: React.ReactNode, width?: number}} props - Props.
 * @returns {JSX.Element}
 */
const TbButton = ({ label, shortcut, active = false, disabled = false, onClick, children, width = 32, ...rest }) => (
  <Tooltip title={shortcut ? `${label} (${shortcut})` : label} disableInteractive>
    <span>
      <ButtonBase
        aria-label={label}
        aria-pressed={active || undefined}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        sx={{
          width,
          height: 32,
          borderRadius: '8px',
          // Neutral pressed state (violet is reserved for AI surfaces and the primary action): a
          // darker fill plus a ring with >= 3:1 contrast that hover never shows.
          color: 'text.primary',
          bgcolor: active ? 'var(--q-control-selected, #DCD8D0)' : 'transparent',
          boxShadow: active ? 'inset 0 0 0 1px var(--q-control-selected-ring, #7A746B)' : 'none',
          opacity: disabled ? 0.4 : 1,
          '&:hover': { bgcolor: active ? 'var(--q-control-selected, #DCD8D0)' : 'var(--q-border, rgba(0,0,0,0.06))' },
          '&.Mui-focusVisible': { outline: '2px solid var(--q-accent, #4F3BD6)', outlineOffset: 1 },
          '& svg': { fontSize: 19 },
        }}
        {...rest}
      >
        {children}
      </ButtonBase>
    </span>
  </Tooltip>
);

const Sep = () => (
  <Box aria-hidden sx={{ width: '1px', height: 20, bgcolor: 'var(--q-border-strong, #D3CFC7)', mx: 0.75, flexShrink: 0 }} />
);

const MOD = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '') ? 'Cmd' : 'Ctrl';

/**
 * Compact single-row editor toolbar, grouped as Undo/Redo | Style | Font | Size | B I U S |
 * Colour, Highlight | Lists | Align | Link, Image, Table | AI field. Groups that do not fit
 * move into the "More" menu (measured with ResizeObserver), so it never wraps into multiple
 * rows. Less frequent tools (lock, variables, task list, find, outline, page width, Ask AI,
 * Save now, shortcuts and host extras) always live in that menu.
 *
 * @param {object} props - Props.
 * @param {import('@tiptap/core').Editor} props.editor - Editor.
 * @param {'document'|'template'} [props.mode] - Editor mode.
 * @param {boolean} [props.enableAdvancedFormatting] - Show font family/size/color.
 * @param {boolean} [props.enableImages] - Allow image insertion.
 * @param {() => void} [props.onPickImage] - Open the image file picker.
 * @param {() => void} [props.onEditImage] - Edit the selected image's alt text.
 * @param {() => void} [props.onOpenFind] - Open find & replace.
 * @param {() => void} [props.onOpenInlineAi] - Open inline AI.
 * @param {() => void} [props.onToggleOutline] - Toggle the outline panel.
 * @param {boolean} [props.outlineOpen] - Whether the outline is open.
 * @param {string|null} [props.outlineUnavailable] - Why the outline cannot show right now (the menu item is disabled with this hint).
 * @param {'page'|'wide'|'pages'} [props.pageMode] - Page view: 'pages' (print layout), 'page' (page width) or 'wide' (full width).
 * @param {(mode: 'page'|'wide'|'pages') => void} [props.onSetPageMode] - Choose the page view (More menu radios).
 * @param {'letter'|'a4'} [props.paperSize] - Pages view paper size.
 * @param {(size: 'letter'|'a4') => void} [props.onSetPaperSize] - Choose the paper size (shown in Pages view).
 * @param {() => void} [props.onShowShortcuts] - Show the shortcut sheet.
 * @param {() => void} [props.onSave] - "Save now" in the More menu (hosts autosave and show the status).
 * @param {Array<{key: string, label: string, icon?: React.ReactNode, onClick: () => void}>} [props.menuExtras] - Host items appended to the More menu.
 * @param {boolean} [props.floating] - Render as the floating mini toolbar (focus mode).
 * @returns {JSX.Element|null}
 */
const EditorToolbar = ({
  editor,
  mode = 'document',
  enableAdvancedFormatting = true,
  enableImages = true,
  onPickImage,
  onEditImage,
  onOpenFind,
  onOpenInlineAi,
  onToggleOutline,
  outlineOpen = false,
  outlineUnavailable = null,
  pageMode = 'page',
  onSetPageMode,
  paperSize = 'letter',
  onSetPaperSize,
  onShowShortcuts,
  onSave,
  menuExtras = null,
  floating = false,
}) => {
  const s = useEditorState({ editor, selector: selectToolbarState });
  const rootRef = useRef(null);
  const [width, setWidth] = useState(1200);
  const [menu, setMenu] = useState({ type: null, anchor: null });
  const [linkDraft, setLinkDraft] = useState('');

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0].contentRect.width);
      setWidth((prev) => (Math.abs(prev - next) > 4 ? next : prev));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const openMenu = useCallback((type) => (event) => setMenu({ type, anchor: event.currentTarget }), []);
  const closeMenu = useCallback(() => setMenu({ type: null, anchor: null }), []);
  const run = useCallback((fn) => () => {
    fn(editor.chain().focus()).run();
  }, [editor]);

  const isTemplate = mode === 'template';

  const groups = useMemo(() => {
    if (!s) return [];
    const list = [
      { id: 'history', width: 72, always: true },
      { id: 'turnInto', width: 124, always: true },
      enableAdvancedFormatting && { id: 'font', width: 108 },
      enableAdvancedFormatting && { id: 'size', width: 104 },
      { id: 'marks', width: 140, always: true },
      enableAdvancedFormatting && { id: 'color', width: 72 },
      { id: 'lists', width: 72 },
      { id: 'align', width: 46 },
      { id: 'insert', width: enableImages ? 112 : 78 },
      isTemplate && { id: 'template', width: 104 },
    ].filter(Boolean);
    return list;
  }, [s, enableAdvancedFormatting, enableImages, isTemplate]);

  const rightWidth = 40;
  const { visible, hidden } = useMemo(() => {
    let used = 0;
    const shown = new Set();
    const overflow = [];
    const budget = Math.max(0, width - rightWidth - 16);
    groups.forEach((group) => {
      const cost = group.width + 14;
      if (overflow.length === 0 && (used + cost <= budget || (group.always && used + cost <= budget + 40))) {
        used += cost;
        shown.add(group.id);
      } else {
        overflow.push(group.id);
      }
    });
    return { visible: shown, hidden: overflow };
  }, [groups, width, rightWidth]);

  if (!editor || !s) return null;

  /**
   * Apply a block type from the "Turn into" menu.
   * @param {string} value - Block type key.
   * @returns {void}
   */
  const applyTurnInto = (value) => {
    const chain = editor.chain().focus();
    switch (value) {
      case 'h1': chain.setHeading({ level: 1 }).run(); break;
      case 'h2': chain.setHeading({ level: 2 }).run(); break;
      case 'h3': chain.setHeading({ level: 3 }).run(); break;
      case 'bulletList': if (!s.bulletList) chain.toggleBulletList().run(); break;
      case 'orderedList': if (!s.orderedList) chain.toggleOrderedList().run(); break;
      case 'taskList': if (!s.taskList) chain.toggleTaskList().run(); break;
      case 'blockquote': if (s.turnInto !== 'blockquote') chain.toggleBlockquote().run(); break;
      case 'codeBlock': chain.toggleCodeBlock().run(); break;
      default:
        if (s.bulletList) chain.toggleBulletList();
        else if (s.orderedList) chain.toggleOrderedList();
        else if (s.taskList) chain.toggleTaskList();
        chain.setParagraph().run();
    }
    closeMenu();
  };

  /**
   * Set (or reset) the font size.
   * @param {number} size - Size in px.
   * @returns {void}
   */
  const setFontSize = (size) => {
    const clamped = Math.max(6, Math.min(96, size));
    if (clamped === 16) editor.chain().focus().unsetFontSize().run();
    else editor.chain().focus().setFontSize(`${clamped}px`).run();
  };

  /**
   * Step the font size to the next/previous preset.
   * @param {1|-1} dir - Direction.
   * @returns {void}
   */
  const stepFontSize = (dir) => {
    const current = s.fontSize;
    const next = dir > 0 ? FONT_SIZES.find((v) => v > current) : [...FONT_SIZES].reverse().find((v) => v < current);
    setFontSize(next || current);
  };

  /** Apply the link popover's URL. */
  const applyLink = () => {
    const href = linkDraft.trim();
    if (!href) editor.chain().focus().extendMarkRange('link').unsetLink().run();
    else {
      const safe = /^(https?:|mailto:|tel:|#|\/)/i.test(href) ? href : `https://${href}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href: safe }).run();
    }
    closeMenu();
  };

  const renderGroup = (id) => {
    switch (id) {
      case 'history':
        return (
          <>
            <TbButton label="Undo" shortcut={`${MOD}+Z`} disabled={!s.canUndo} onClick={run((c) => c.undo())}><Undo /></TbButton>
            <TbButton label="Redo" shortcut={`${MOD}+Shift+Z`} disabled={!s.canRedo} onClick={run((c) => c.redo())}><Redo /></TbButton>
          </>
        );
      case 'turnInto':
        return (
          <ButtonBase
            aria-label="Text style"
            aria-haspopup="menu"
            onMouseDown={(e) => e.preventDefault()}
            onClick={openMenu('turnInto')}
            sx={{ width: 124, height: 32, px: 1, borderRadius: '8px', justifyContent: 'space-between', fontSize: 13, color: 'text.primary', '&:hover': { bgcolor: 'var(--q-border, rgba(0,0,0,0.06))' } }}
          >
            <span>{(TURN_INTO.find((t) => t.value === s.turnInto) || TURN_INTO[0]).label}</span>
            <ArrowDropDown sx={{ fontSize: 18 }} />
          </ButtonBase>
        );
      case 'font':
        return (
          <ButtonBase
            aria-label="Font"
            aria-haspopup="menu"
            onMouseDown={(e) => e.preventDefault()}
            onClick={openMenu('font')}
            sx={{ width: 108, height: 32, px: 1, borderRadius: '8px', justifyContent: 'space-between', fontSize: 13, color: 'text.primary', '&:hover': { bgcolor: 'var(--q-border, rgba(0,0,0,0.06))' } }}
          >
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.fontName}</Box>
            <ArrowDropDown sx={{ fontSize: 18 }} />
          </ButtonBase>
        );
      case 'size':
        return (
          <>
            <TbButton label="Decrease font size" width={28} onClick={() => stepFontSize(-1)}>
              <Typography component="span" sx={{ fontSize: 16 }}>−</Typography>
            </TbButton>
            <ButtonBase
              aria-label={`Font size ${s.fontSize}`}
              aria-haspopup="menu"
              onMouseDown={(e) => e.preventDefault()}
              onClick={openMenu('size')}
              sx={{ width: 36, height: 26, border: '1px solid', borderColor: 'var(--q-border-strong, #C9C4BB)', borderRadius: '6px', bgcolor: 'background.paper', fontSize: 13 }}
            >
              {s.fontSize}
            </ButtonBase>
            <TbButton label="Increase font size" width={28} onClick={() => stepFontSize(1)}>
              <Typography component="span" sx={{ fontSize: 16 }}>+</Typography>
            </TbButton>
          </>
        );
      case 'marks':
        return (
          <>
            <TbButton label="Bold" shortcut={`${MOD}+B`} active={s.bold} onClick={run((c) => c.toggleBold())}><FormatBold /></TbButton>
            <TbButton label="Italic" shortcut={`${MOD}+I`} active={s.italic} onClick={run((c) => c.toggleItalic())}><FormatItalic /></TbButton>
            <TbButton label="Underline" shortcut={`${MOD}+U`} active={s.underline} onClick={run((c) => c.toggleUnderline())}><FormatUnderlined /></TbButton>
            <TbButton label="Strikethrough" shortcut={`${MOD}+Shift+S`} active={s.strike} onClick={run((c) => c.toggleStrike())}><FormatStrikethrough /></TbButton>
          </>
        );
      case 'color':
        return (
          <>
            <TbButton label="Text color" onClick={openMenu('textColor')}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                <Typography component="span" sx={{ fontSize: 15, lineHeight: 1 }}>A</Typography>
                <Box sx={{ width: 16, height: 3, borderRadius: '1px', bgcolor: s.color || 'text.primary', mt: '1px' }} />
              </Box>
            </TbButton>
            <TbButton label="Highlight" onClick={openMenu('highlight')}><FormatColorFill /></TbButton>
          </>
        );
      case 'lists':
        return (
          <>
            <TbButton label="Bulleted list" shortcut={`${MOD}+Shift+8`} active={s.bulletList} onClick={run((c) => c.toggleBulletList())}><FormatListBulleted /></TbButton>
            <TbButton label="Numbered list" shortcut={`${MOD}+Shift+7`} active={s.orderedList} onClick={run((c) => c.toggleOrderedList())}><FormatListNumbered /></TbButton>
          </>
        );
      case 'align': {
        const AlignIcon = s.isImage
          ? ({ left: FormatAlignLeft, center: FormatAlignCenter, right: FormatAlignRight }[s.imageAlign] || FormatAlignCenter)
          : ({ left: FormatAlignLeft, center: FormatAlignCenter, right: FormatAlignRight, justify: FormatAlignJustify }[s.align] || FormatAlignLeft);
        return (
          <TbButton label={s.isImage ? 'Image alignment' : 'Alignment'} width={42} onClick={openMenu('align')}>
            <AlignIcon />
            <ArrowDropDown sx={{ fontSize: '16px !important', ml: -0.5 }} />
          </TbButton>
        );
      }
      case 'insert':
        return (
          <>
            <TbButton
              label="Link"
              active={s.link}
              onClick={(e) => {
                setLinkDraft(s.linkHref || '');
                openMenu('link')(e);
              }}
            >
              <LinkIcon />
            </TbButton>
            {enableImages && onPickImage && (
              <TbButton label="Insert image" active={s.isImage} onClick={onPickImage}><ImageIcon /></TbButton>
            )}
            <TbButton label={s.inTable ? 'Table options' : 'Insert table'} active={s.inTable} onClick={openMenu(s.inTable ? 'tableOps' : 'tableGrid')}>
              <TableChart />
            </TbButton>
          </>
        );
      case 'template':
        // The one AI entry point on the toolbar: light AI tint, not a filled primary button.
        return (
          <Button
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().insertTemplateField().run()}
            startIcon={<AutoAwesome sx={{ fontSize: '16px !important' }} />}
            sx={{
              height: 32,
              px: 1.5,
              borderRadius: 999,
              border: '1px dashed var(--q-ai-border, #C9C1F2)',
              bgcolor: 'var(--q-ai-tint, #F9F8FE)',
              color: 'var(--q-ai-text, #3A28B8)',
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              textTransform: 'none',
              '&:hover': { bgcolor: 'var(--q-ai-tint-strong, #F2EFFD)' },
            }}
          >
            AI field
          </Button>
        );
      default:
        return null;
    }
  };

  /**
   * Overflow menu entries for hidden groups plus extras that always live in the menu.
   * @returns {React.ReactNode[]} Menu items.
   */
  const overflowItems = () => {
    const items = [];
    const item = (key, icon, label, onClick, opts = {}) => (
      <MenuItem
        key={key}
        dense
        disabled={opts.disabled}
        selected={opts.selected}
        role={opts.checked === undefined ? undefined : 'menuitemradio'}
        aria-checked={opts.checked === undefined ? undefined : String(Boolean(opts.checked))}
        aria-label={opts.ariaLabel}
        onClick={() => { closeMenu(); onClick(); }}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText
          primary={label}
          secondary={opts.hint || undefined}
          secondaryTypographyProps={{ sx: { whiteSpace: 'normal', maxWidth: 240 } }}
        />
        {opts.shortcut && (
          <Typography component="span" variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>{opts.shortcut}</Typography>
        )}
        {opts.checked && <Check fontSize="small" aria-hidden="true" sx={{ ml: 2 }} />}
      </MenuItem>
    );
    hidden.forEach((id) => {
      if (id === 'font') items.push(item('font', <Typography sx={{ fontSize: 13 }}>Aa</Typography>, `Font: ${s.fontName}`, () => setMenu({ type: 'font', anchor: rootRef.current })));
      if (id === 'size') items.push(item('size', <Typography sx={{ fontSize: 13 }}>{s.fontSize}</Typography>, 'Font size', () => setMenu({ type: 'size', anchor: rootRef.current })));
      if (id === 'color') {
        items.push(item('textColor', <Typography sx={{ fontSize: 14, color: s.color || 'inherit' }}>A</Typography>, 'Text color', () => setMenu({ type: 'textColor', anchor: rootRef.current })));
        items.push(item('highlight', <FormatColorFill fontSize="small" />, 'Highlight', () => setMenu({ type: 'highlight', anchor: rootRef.current })));
      }
      if (id === 'lists') {
        items.push(item('bullets', <FormatListBulleted fontSize="small" />, 'Bulleted list', run((c) => c.toggleBulletList()), { selected: s.bulletList }));
        items.push(item('numbers', <FormatListNumbered fontSize="small" />, 'Numbered list', run((c) => c.toggleOrderedList()), { selected: s.orderedList }));
      }
      if (id === 'align') items.push(item('align', <FormatAlignLeft fontSize="small" />, 'Alignment', () => setMenu({ type: 'align', anchor: rootRef.current })));
      if (id === 'insert') {
        items.push(item('link', <LinkIcon fontSize="small" />, 'Link', () => { setLinkDraft(s.linkHref || ''); setMenu({ type: 'link', anchor: rootRef.current }); }));
        if (enableImages && onPickImage) items.push(item('image', <ImageIcon fontSize="small" />, 'Image', onPickImage));
        items.push(item('table', <TableChart fontSize="small" />, s.inTable ? 'Table options' : 'Table', () => setMenu({ type: s.inTable ? 'tableOps' : 'tableGrid', anchor: rootRef.current })));
      }
      if (id === 'template') {
        items.push(item('field', <AutoAwesome fontSize="small" />, 'Insert AI field', run((c) => c.insertTemplateField())));
      }
    });
    if (items.length) items.push(<Divider key="d1" />);
    if (isTemplate) {
      items.push(item('variable', <DataObject fontSize="small" />, 'Insert variable', () => setMenu({ type: 'variable', anchor: rootRef.current })));
      items.push(item('lock', s.locked ? <Lock fontSize="small" /> : <LockOpen fontSize="small" />, s.locked ? 'Unlock block (AI may edit it)' : 'Lock block (AI will not change it)', run((c) => c.toggleBlockLock()), { selected: s.locked }));
      items.push(<Divider key="d-template" />);
    }
    items.push(item('code', <Code fontSize="small" />, 'Inline code', run((c) => c.toggleCode()), { selected: s.code }));
    items.push(item('sub', <SubscriptIcon fontSize="small" />, 'Subscript', run((c) => c.toggleSubscript()), { selected: s.subscript }));
    items.push(item('sup', <SuperscriptIcon fontSize="small" />, 'Superscript', run((c) => c.toggleSuperscript()), { selected: s.superscript }));
    items.push(item('quote', <FormatQuote fontSize="small" />, 'Quote', run((c) => c.toggleBlockquote())));
    items.push(item('codeBlock', <Code fontSize="small" />, 'Code block', run((c) => c.toggleCodeBlock())));
    items.push(item('divider', <HorizontalRule fontSize="small" />, 'Divider', run((c) => c.setHorizontalRule())));
    items.push(item('pageBreak', <InsertPageBreak fontSize="small" />, 'Page break', run((c) => c.insertPageBreak()), { shortcut: `${MOD}+Enter` }));
    items.push(item('mermaid', <AccountTree fontSize="small" />, 'Mermaid diagram', run((c) => c.insertMermaidBlock())));
    items.push(item('tasks', <Checklist fontSize="small" />, 'Task list', run((c) => c.toggleTaskList()), { selected: s.taskList, shortcut: `${MOD}+Shift+9` }));
    if (s.inList) {
      items.push(item('outdent', <FormatIndentDecrease fontSize="small" />, 'Decrease indent', run((c) => (s.taskList ? c.liftListItem('taskItem') : c.liftListItem('listItem')))));
      items.push(item('indent', <FormatIndentIncrease fontSize="small" />, 'Increase indent', run((c) => (s.taskList ? c.sinkListItem('taskItem') : c.sinkListItem('listItem')))));
    }
    if (s.isImage && onEditImage) items.push(item('imgAlt', <EditIcon fontSize="small" />, 'Image alt text', onEditImage));
    if (!floating) {
      const view = [];
      if (onOpenFind) view.push(item('find', <FindInPage fontSize="small" />, 'Find and replace', onOpenFind, { shortcut: `${MOD}+F` }));
      if (onToggleOutline) {
        view.push(outlineUnavailable
          ? item('outline', <Toc fontSize="small" />, 'Show outline', onToggleOutline, { disabled: true, hint: outlineUnavailable })
          : item('outline', <Toc fontSize="small" />, outlineOpen ? 'Hide outline' : 'Show outline', onToggleOutline, { selected: outlineOpen }));
      }
      if (onSetPageMode) {
        // Same radios (and order) as the document header's view menu.
        view.push(item('pm-pages', <ViewAgenda fontSize="small" />, 'Pages', () => onSetPageMode('pages'), { checked: pageMode === 'pages' }));
        view.push(item('pm-page', <WidthNormal fontSize="small" />, 'Page width', () => onSetPageMode('page'), { checked: pageMode === 'page' }));
        view.push(item('pm-wide', <WidthFull fontSize="small" />, 'Full width', () => onSetPageMode('wide'), { checked: pageMode === 'wide' }));
        if (pageMode === 'pages' && onSetPaperSize) {
          // A separate radio set: its own divider and heading, and names that say what it sets.
          view.push(<Divider key="d-paper" />);
          view.push(
            <ListSubheader key="paper-heading" aria-hidden="true" sx={{ lineHeight: '32px', bgcolor: 'transparent' }}>
              Paper size
            </ListSubheader>
          );
          view.push(item('ps-letter', null, 'Letter', () => onSetPaperSize('letter'), { checked: paperSize === 'letter', hint: '8.5 x 11 in', ariaLabel: 'Letter paper size' }));
          view.push(item('ps-a4', null, 'A4', () => onSetPaperSize('a4'), { checked: paperSize === 'a4', hint: '210 x 297 mm', ariaLabel: 'A4 paper size' }));
          view.push(<Divider key="d-paper-end" />);
        }
      }
      if (onOpenInlineAi) view.push(item('askAi', <AutoAwesome fontSize="small" />, 'Ask AI about the selection', onOpenInlineAi, { shortcut: `${MOD}+J` }));
      if (onSave) view.push(item('save', <SaveIcon fontSize="small" />, 'Save now', onSave, { shortcut: `${MOD}+S` }));
      if (view.length) items.push(<Divider key="d-view" />, ...view);
    }
    const extras = (menuExtras || []).filter(Boolean);
    if (extras.length || onShowShortcuts) items.push(<Divider key="d2" />);
    extras.forEach((extra) => items.push(item(`x-${extra.key}`, extra.icon || null, extra.label, extra.onClick)));
    if (onShowShortcuts) items.push(item('shortcuts', <Keyboard fontSize="small" />, 'Keyboard shortcuts', onShowShortcuts));
    return items;
  };

  const colorGrid = (colors, onPick) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 28px)', gap: 0.75, p: 1.5 }}>
      {colors.map((color) => (
        <ButtonBase
          key={color}
          aria-label={color === 'transparent' ? 'No color' : color}
          onClick={() => { onPick(color); closeMenu(); }}
          sx={{
            width: 28,
            height: 28,
            borderRadius: '6px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: color === 'transparent' ? 'background.paper' : color,
            backgroundImage: color === 'transparent' ? 'linear-gradient(135deg, transparent 45%, #D63B1F 45%, #D63B1F 55%, transparent 55%)' : 'none',
            '&:hover': { transform: 'scale(1.08)' },
          }}
        />
      ))}
    </Box>
  );

  const containerSx = floating
    ? {
      display: 'flex',
      alignItems: 'center',
      gap: '2px',
      px: 0.75,
      py: 0.5,
      borderRadius: 999,
      bgcolor: 'var(--q-surface, #FFFFFF)',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 6px 20px rgba(28,27,25,0.14)',
    }
    : {
      display: 'flex',
      alignItems: 'center',
      gap: '2px',
      px: 1,
      py: 0.5,
      mb: 1,
      borderRadius: 999,
      bgcolor: 'var(--q-subtle, #F1EFEB)',
      position: 'sticky',
      top: 'var(--q-editor-sticky-top, 0px)',
      zIndex: 5,
      minWidth: 0,
      overflow: 'hidden',
    };

  return (
    <Box ref={rootRef} role="toolbar" aria-label="Formatting" sx={containerSx}>
      {groups.filter((g) => visible.has(g.id)).map((group, index) => (
        <React.Fragment key={group.id}>
          {index > 0 && <Sep />}
          {renderGroup(group.id)}
        </React.Fragment>
      ))}
      <Box sx={{ flex: 1, minWidth: 4 }} />
      <TbButton
        label="More"
        aria-haspopup="menu"
        aria-expanded={menu.type === 'overflow' ? 'true' : undefined}
        onClick={openMenu('overflow')}
      >
        <MoreHoriz />
      </TbButton>

      <Menu open={menu.type === 'overflow'} anchorEl={menu.anchor} onClose={closeMenu} MenuListProps={{ dense: true }}>
        {overflowItems()}
      </Menu>

      <Menu open={menu.type === 'turnInto'} anchorEl={menu.anchor} onClose={closeMenu}>
        {TURN_INTO.map((option) => (
          <MenuItem key={option.value} dense selected={option.value === s.turnInto} onClick={() => applyTurnInto(option.value)}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      <Menu open={menu.type === 'font'} anchorEl={menu.anchor} onClose={closeMenu}>
        {FONT_FAMILIES.map((font) => (
          <MenuItem
            key={font.name}
            dense
            selected={font.name === s.fontName}
            sx={{ fontFamily: font.value }}
            onClick={() => {
              if (font.name === 'Default') editor.chain().focus().unsetFontFamily().run();
              else editor.chain().focus().setFontFamily(font.value).run();
              closeMenu();
            }}
          >
            {font.name}
          </MenuItem>
        ))}
      </Menu>

      <Menu open={menu.type === 'size'} anchorEl={menu.anchor} onClose={closeMenu} PaperProps={{ sx: { maxHeight: 320 } }}>
        {FONT_SIZES.map((size) => (
          <MenuItem key={size} dense selected={size === s.fontSize} onClick={() => { setFontSize(size); closeMenu(); }}>
            {size}
          </MenuItem>
        ))}
      </Menu>

      <Menu open={menu.type === 'textColor'} anchorEl={menu.anchor} onClose={closeMenu}>
        {colorGrid(['transparent', ...TEXT_COLORS.slice(0, 19)], (color) => {
          if (color === 'transparent') editor.chain().focus().unsetColor().run();
          else editor.chain().focus().setColor(color).run();
        })}
      </Menu>

      <Menu open={menu.type === 'highlight'} anchorEl={menu.anchor} onClose={closeMenu}>
        {colorGrid(HIGHLIGHT_COLORS, (color) => {
          if (color === 'transparent') editor.chain().focus().unsetHighlight().run();
          else editor.chain().focus().setHighlight({ color }).run();
        })}
      </Menu>

      <Menu open={menu.type === 'align'} anchorEl={menu.anchor} onClose={closeMenu}>
        {(s.isImage
          ? [['left', FormatAlignLeft, 'Image left'], ['center', FormatAlignCenter, 'Image center'], ['right', FormatAlignRight, 'Image right']]
          : [['left', FormatAlignLeft, 'Align left'], ['center', FormatAlignCenter, 'Center'], ['right', FormatAlignRight, 'Align right'], ['justify', FormatAlignJustify, 'Justify']]
        ).map(([value, Icon, label]) => (
          <MenuItem
            key={value}
            dense
            selected={(s.isImage ? s.imageAlign : s.align) === value}
            onClick={() => {
              if (s.isImage) editor.chain().focus().updateAttributes('image', { align: value }).run();
              else editor.chain().focus().setTextAlign(value).run();
              closeMenu();
            }}
          >
            <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <Menu open={menu.type === 'variable'} anchorEl={menu.anchor} onClose={closeMenu}>
        {TEMPLATE_VARIABLES.map((variable) => (
          <MenuItem
            key={variable.name}
            dense
            onClick={() => {
              editor.chain().focus().insertContent(`{{${variable.name}}}`).run();
              closeMenu();
            }}
          >
            <ListItemText primary={variable.label} secondary={`{{${variable.name}}}`} />
          </MenuItem>
        ))}
      </Menu>

      <Menu open={menu.type === 'tableOps'} anchorEl={menu.anchor} onClose={closeMenu}>
        {[
          ['Add column before', AddBox, (c) => c.addColumnBefore()],
          ['Add column after', AddBox, (c) => c.addColumnAfter()],
          ['Delete column', IndeterminateCheckBox, (c) => c.deleteColumn()],
          ['Add row before', TableRows, (c) => c.addRowBefore()],
          ['Add row after', TableRows, (c) => c.addRowAfter()],
          ['Delete row', IndeterminateCheckBox, (c) => c.deleteRow()],
          ['Merge cells', TableView, (c) => c.mergeCells()],
          ['Split cell', TableView, (c) => c.splitCell()],
          ['Toggle header row', TableRows, (c) => c.toggleHeaderRow()],
          ['Delete table', IndeterminateCheckBox, (c) => c.deleteTable()],
        ].map(([label, Icon, fn]) => (
          <MenuItem key={label} dense onClick={() => { fn(editor.chain().focus()).run(); closeMenu(); }}>
            <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <TableInsertGridPopover
        open={menu.type === 'tableGrid'}
        anchorEl={menu.anchor}
        onClose={closeMenu}
        onSelect={(rows, cols) => editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run()}
      />

      <Popover
        open={menu.type === 'link'}
        anchorEl={menu.anchor}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1.5, width: 340, borderRadius: 'var(--q-radius-lg, 12px)' } }}
      >
        <Box component="form" onSubmit={(e) => { e.preventDefault(); applyLink(); }} sx={{ display: 'flex', gap: 1 }}>
          <TextField
            autoFocus
            size="small"
            fullWidth
            placeholder="Paste or type a link"
            value={linkDraft}
            onChange={(e) => setLinkDraft(e.target.value)}
            inputProps={{ 'aria-label': 'Link URL' }}
          />
          <Button type="submit" variant="contained" disableElevation size="small">Apply</Button>
        </Box>
        {s.link && (
          <Button size="small" color="error" sx={{ mt: 1 }} onClick={() => { editor.chain().focus().extendMarkRange('link').unsetLink().run(); closeMenu(); }}>
            Remove link
          </Button>
        )}
      </Popover>
    </Box>
  );
};

export default memo(EditorToolbar);
export { selectToolbarState };
